import { Link, useParams } from 'react-router-dom';
import { useActiveCompanyId } from '@/lib/active-company';
import { useStandardReport } from '@/lib/assessments';
import { useSnapshot, useSnapshots } from '@/lib/snapshots';
import { StatTile } from '@/components/StatTile';
import { ReportHeader } from './_shared/ReportHeader';
import { ModuleRollup } from './_shared/ModuleRollup';
import { Deficiencies } from './_shared/Deficiencies';
import { AgencyStrip } from './_shared/AgencyStrip';
import { aggregate } from './LiveView';
import { catalogStamp } from '@/lib/catalog-stamp';

export function SnapshotView() {
  const { snapshotId } = useParams<{ snapshotId: string }>();
  const companyId = useActiveCompanyId();
  const snapshot = useSnapshot(companyId, snapshotId);
  const snapshots = useSnapshots(companyId);
  // Agency rollup uses LIVE data: snapshot detail returns AssessmentResponseSummary
  // (no per-agency breakdown), so the strip reflects current standard mapping rather
  // than frozen scope. Acceptable trade-off for v1.
  const standardReport = useStandardReport(companyId);

  if (!companyId) return <p className="p-10">Select a company first.</p>;
  if (snapshot.isLoading) return <p className="p-10">Loading…</p>;
  if (!snapshot.data) return <p className="p-10">Snapshot not found.</p>;

  const data = snapshot.data.payload; // backend column is `payload`, see web/src/lib/snapshots.ts
  const totals = aggregate(data.modules);

  // List arrives newest-first; the first snapshot strictly older than the
  // current one is the natural "compare to previous" target.
  const previousSnapshotId = snapshots.data
    ? snapshots.data.find(
        (s) =>
          new Date(s.generatedAt).getTime() <
          new Date(snapshot.data!.generatedAt).getTime(),
      )?.id
    : undefined;

  return (
    <div className="mx-auto flex max-w-content-wide flex-col gap-gap-group px-5 py-gap-group md:px-10 lg:px-20">
      <ReportHeader
        mode="snapshot"
        generatedAt={snapshot.data.generatedAt}
        generatedBy={snapshot.data.generatedBy.name ?? snapshot.data.generatedBy.email}
        snapshotId={snapshot.data.id}
        snapshotName={snapshot.data.name}
        stamp={catalogStamp({
          catalogReleaseVersion: snapshot.data.catalogReleaseVersion,
          asOf: snapshot.data.scoredAsOf,
        })}
      />

      {previousSnapshotId ? (
        <Link
          to={`/report/compare?from=${previousSnapshotId}&to=${snapshot.data.id}`}
          className="-mt-4 self-start text-body-sm font-medium text-accent hover:underline"
        >
          Compare to previous snapshot →
        </Link>
      ) : null}

      {snapshot.data.notes ? (
        <section
          className="rounded-lg border border-border bg-surface p-7"
          data-testid="snapshot-notes"
        >
          <p className="text-label uppercase tracking-[0.08em] text-text-secondary">
            Notes
          </p>
          <p className="mt-3 whitespace-pre-wrap text-body text-text-primary">
            {snapshot.data.notes}
          </p>
        </section>
      ) : null}

      <section className="grid gap-gap-element-lg md:grid-cols-2 lg:grid-cols-4">
        <StatTile
          label="Compliance"
          value={`${totals.compliancePct}%`}
          subtext={`${totals.compliant}/${totals.total}`}
          accent="accent"
          progressPct={totals.compliancePct}
        />
        <StatTile
          label="Items assessed"
          value={String(totals.assessed)}
          subtext={`of ${totals.total}`}
          accent="accent"
          progressPct={totals.total > 0 ? Math.round((totals.assessed / totals.total) * 100) : 0}
        />
        <StatTile
          label="Open warnings"
          value={String(totals.warnings)}
          accent={totals.warnings > 0 ? 'danger' : 'accent'}
          progressPct={totals.total > 0 ? Math.round((totals.warnings / totals.total) * 100) : 0}
        />
        <StatTile
          label="Modules in progress"
          value={String(totals.modulesInProgress)}
          subtext={`of ${data.modules.length}`}
          accent="accent"
          progressPct={
            data.modules.length > 0
              ? Math.round((totals.modulesInProgress / data.modules.length) * 100)
              : 0
          }
        />
      </section>

      <ModuleRollup modules={data.modules} linked={false} showCsvExport={false} />
      <Deficiencies modules={data.modules} linked={false} />
      {standardReport.data && standardReport.data.agencies.length > 0 ? (
        <AgencyStrip
          agencies={standardReport.data.agencies}
          caption="Current standards: today's mapping, not this snapshot's release"
        />
      ) : null}
    </div>
  );
}
